"use client";

import {
  addDays,
  format,
  isBefore,
  isSameDay,
  isToday,
  startOfDay,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  useContext,
  useState,
} from "react";
import { Slot } from "radix-ui";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

// Simplified controllable state helper (avoid extra deps)
const useControllable = <T,>({
  prop,
  defaultProp,
  onChange,
}: {
  prop?: T;
  defaultProp?: T;
  onChange?: (value: T | undefined) => void;
}): [T | undefined, (value: T | undefined) => void] => {
  const [internal, setInternal] = useState<T | undefined>(
    prop !== undefined ? prop : defaultProp
  );
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : internal;
  const setValue = (next: T | undefined) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };
  return [value, setValue];
};

type MiniCalendarContextType = {
  selectedDate: Date | null | undefined;
  onDateSelect: (date: Date) => void;
  startDate: Date;
  onNavigate: (direction: "prev" | "next") => void;
  days: number;
  minDate?: Date;
};

const MiniCalendarContext = createContext<MiniCalendarContextType | null>(null);

const useMiniCalendar = () => {
  const context = useContext(MiniCalendarContext);
  if (!context) {
    throw new Error("MiniCalendar components must be used within MiniCalendar");
  }
  return context;
};

const getDays = (startDate: Date, count: number): Date[] => {
  const days: Date[] = [];
  for (let i = 0; i < count; i++) {
    days.push(addDays(startDate, i));
  }
  return days;
};

const formatMonthDay = (date: Date) => {
  const month = format(date, "MMM");
  const day = format(date, "d");
  return { month, day };
};

export type MiniCalendarProps = HTMLAttributes<HTMLDivElement> & {
  value?: Date;
  defaultValue?: Date;
  onValueChange?: (date: Date | undefined) => void;
  startDate?: Date;
  defaultStartDate?: Date;
  onStartDateChange?: (date: Date | undefined) => void;
  days?: number;
  minDate?: Date;
};

export const MiniCalendar = ({
  value,
  defaultValue,
  onValueChange,
  startDate,
  defaultStartDate = new Date(),
  onStartDateChange,
  days = 5,
  minDate,
  className,
  children,
  ...props
}: MiniCalendarProps) => {
  const [selectedDate, setSelectedDate] = useControllable<Date>({
    prop: value,
    defaultProp: defaultValue,
    onChange: onValueChange,
  });

  const [currentStartDate, setCurrentStartDate] = useControllable<Date>({
    prop: startDate,
    defaultProp: defaultStartDate,
    onChange: onStartDateChange,
  });

  const normalizedMinDate = minDate ? startOfDay(minDate) : undefined;

  const handleDateSelect = (date: Date) => {
    if (normalizedMinDate && isBefore(date, normalizedMinDate)) {
      return;
    }
    setSelectedDate(date);
  };

  const handleNavigate = (direction: "prev" | "next") => {
    const base = currentStartDate || new Date();
    const newStartDate = addDays(base, direction === "next" ? days : -days);
    if (normalizedMinDate && isBefore(newStartDate, normalizedMinDate)) {
      setCurrentStartDate(normalizedMinDate);
      return;
    }
    setCurrentStartDate(newStartDate);
  };

  const contextValue: MiniCalendarContextType = {
    selectedDate: selectedDate || null,
    onDateSelect: handleDateSelect,
    startDate: currentStartDate || new Date(),
    onNavigate: handleNavigate,
    days,
    minDate: normalizedMinDate,
  };

  return (
    <MiniCalendarContext.Provider value={contextValue}>
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </MiniCalendarContext.Provider>
  );
};

export type MiniCalendarNavigationProps =
  ButtonHTMLAttributes<HTMLButtonElement> & {
    direction: "prev" | "next";
    asChild?: boolean;
  };

export const MiniCalendarNavigation = ({
  direction,
  asChild = false,
  children,
  onClick,
  ...props
}: MiniCalendarNavigationProps) => {
  const { onNavigate, startDate, minDate } = useMiniCalendar();
  const Icon = direction === "prev" ? ChevronLeftIcon : ChevronRightIcon;

  const isPrevDisabled =
    direction === "prev" &&
    minDate &&
    (isSameDay(startDate, minDate) || isBefore(startDate, startOfDay(minDate)));

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (isPrevDisabled) return;
    onNavigate(direction);
    onClick?.(event);
  };

  if (asChild) {
    return (
      <Slot.Root onClick={handleClick} {...props}>
        {children}
      </Slot.Root>
    );
  }

  return (
    <Button
      onClick={handleClick}
      size="icon"
      type="button"
      variant="ghost"
      disabled={isPrevDisabled || props.disabled}
      className={cn(
        "text-slate-600 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-200",
        props.className,
        isPrevDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent"
      )}
      {...props}
    >
      {children ?? <Icon className="h-4 w-4" />}
    </Button>
  );
};

export type MiniCalendarDaysProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  children: (date: Date) => ReactNode;
};

export const MiniCalendarDays = ({
  className,
  children,
  ...props
}: MiniCalendarDaysProps) => {
  const { startDate, days: dayCount } = useMiniCalendar();
  const days = getDays(startDate, dayCount);
  return (
    <div className={cn("flex items-center gap-1", className)} {...props}>
      {days.map((date) => children(date))}
    </div>
  );
};

export type MiniCalendarDayProps = ComponentProps<typeof Button> & {
  date: Date;
};

export const MiniCalendarDay = ({
  date,
  className,
  ...props
}: MiniCalendarDayProps) => {
  const { selectedDate, onDateSelect, minDate } = useMiniCalendar();
  const { month, day } = formatMonthDay(date);
  const isSelected = selectedDate && isSameDay(date, selectedDate);
  const isTodayDate = isToday(date);
  const isDisabled = minDate && isBefore(date, startOfDay(minDate));

  return (
    <Button
      className={cn(
        "h-auto min-w-[3rem] flex-col gap-0 p-2 text-xs border border-slate-200 bg-white text-slate-900 hover:bg-slate-50",
        isTodayDate && !isSelected && "bg-slate-100",
        isSelected &&
          "border border-[#326296] bg-[#326296] text-white hover:bg-[#326296] hover:text-white",
        isDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
        className
      )}
      onClick={() => !isDisabled && onDateSelect(date)}
      size="sm"
      type="button"
      variant="ghost"
      disabled={isDisabled || props.disabled}
      style={
        isSelected
          ? { backgroundColor: "#326296", borderColor: "#326296", color: "white" }
          : undefined
      }
      {...props}
    >
      <span
        className={cn(
          "font-medium text-[10px] text-slate-500",
          isSelected && "text-white/90"
        )}
      >
        {month}
      </span>
      <span
        className={cn(
          "font-semibold text-sm",
          isSelected ? "text-white" : "text-slate-900"
        )}
      >
        {day}
      </span>
    </Button>
  );
};
